import { useEffect } from 'react';
import { useAgentState } from '@/context/agent-state.context';
import { rpcClient } from 'src/api/rpc/client';
import { renderSpecToImage } from '@/utils/render-spec-to-image';
import type { InternalUiSpec } from '@erp/json-renderer/react';

/**
 * Handles the agent's `preview_custom_view` tool call: renders the spec
 * off-screen, screenshots it, and POSTs the image back so the agent can verify
 * the layout before presenting. Mounted app-wide (in ChatAgentRenderer) so it
 * works on every chat surface, including the inbox home. Mirrors
 * useCaptureScreenHandler.
 */
export function usePreviewCustomViewHandler() {
  const { registerToolCallListener } = useAgentState();

  useEffect(() => {
    return registerToolCallListener((toolCallId, toolName, args) => {
      if (toolName !== 'preview_custom_view') return;

      const spec = args.spec as InternalUiSpec | undefined;
      const state =
        args.state &&
        typeof args.state === 'object' &&
        !Array.isArray(args.state)
          ? (args.state as Record<string, unknown>)
          : {};

      if (!spec || typeof spec !== 'object') {
        void rpcClient.ai.chatAgent.submitFrontendToolResult({
          toolCallId,
          result: { error: 'No spec provided to preview.' },
        });
        return;
      }

      renderSpecToImage(spec, state)
        .then((result) =>
          rpcClient.ai.chatAgent.submitFrontendToolResult({
            toolCallId,
            result,
          }),
        )
        .catch((err: unknown) => {
          console.error('[preview-custom-view] render failed:', err);
          void rpcClient.ai.chatAgent.submitFrontendToolResult({
            toolCallId,
            result: { error: 'Preview render failed.' },
          });
        });
    });
  }, [registerToolCallListener]);
}
